import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

const copyByLang = {
  es: {
    title: "Preguntas Frecuentes | Puerto Rico Esports League",
    description: "Encuentra respuestas a preguntas frecuentes sobre Puerto Rico Esports League, su programa, eventos, aliados y formas de conectar con la plataforma.",
    keywords: "Puerto Rico Esports League FAQ, qué es PREL, esports Puerto Rico, gaming Puerto Rico, PREL marcas, PREL eventos",
    ogDescription: "Respuestas claras sobre PREL, su contenido, eventos y oportunidades de colaboración.",
    eyebrow: "Preguntas Frecuentes",
    heroTitle: "Respuestas claras sobre PREL",
    heroBody:
      "Esta página responde las preguntas más comunes sobre qué es Puerto Rico Esports League, cómo opera, qué tipo de contenido produce y cómo pueden conectar con la plataforma marcas, medios, instituciones y audiencias.",
    items: [
      {
        q: "¿Qué es Puerto Rico Esports League?",
        a: "Puerto Rico Esports League (PREL) es una plataforma de esports y entretenimiento en Puerto Rico que produce eventos en vivo, contenido original y programación televisiva alrededor de la cultura competitiva.",
      },
      {
        q: "¿PREL es solo una liga de torneos?",
        a: "No. Aunque la competencia es parte importante del proyecto, PREL también trabaja contenido, televisión, activaciones en vivo, colaboraciones de marca y desarrollo de comunidad.",
      },
      {
        q: "¿Dónde puedo ver contenido de PREL?",
        a: "El contenido de PREL vive a través de su programa oficial, su canal de YouTube, sus redes sociales, recaps de eventos y materiales oficiales publicados en sus plataformas digitales.",
      },
      {
        q: "¿PREL produce eventos en vivo en Puerto Rico?",
        a: "Sí. PREL organiza y apoya eventos de gaming y esports en Puerto Rico, incluyendo torneos, activaciones de marca, experiencias presenciales y coberturas en formato audiovisual.",
      },
      {
        q: "¿PREL trabaja con marcas, medios e instituciones?",
        a: "Sí. PREL colabora con marcas, instituciones, medios y aliados estratégicos mediante auspicios, contenido de marca, activaciones, visibilidad digital y proyectos especiales.",
      },
      {
        q: "¿Quién lidera Puerto Rico Esports League?",
        a: "PREL es liderado por Dwight Santana, presidente y productor ejecutivo del proyecto, junto a un equipo enfocado en eventos, contenido, alianzas estratégicas y crecimiento del ecosistema.",
      },
      {
        q: "¿Cómo puede una marca colaborar con PREL?",
        a: "Una marca puede colaborar con PREL a través de auspicios, integraciones de contenido, presencia en eventos, activaciones presenciales o conversaciones estratégicas adaptadas a sus objetivos.",
      },
      {
        q: "¿PREL tiene alcance más allá de Puerto Rico?",
        a: "Sí. Aunque nace en Puerto Rico, PREL está construyendo una plataforma con potencial para conectar con el mercado hispano en Estados Unidos, Latinoamérica y oportunidades internacionales.",
      },
    ],
  },
  en: {
    title: "FAQ | Puerto Rico Esports League",
    description: "Find answers to frequently asked questions about Puerto Rico Esports League, its show, events, partners, and ways to connect with the platform.",
    keywords: "Puerto Rico Esports League FAQ, what is PREL, Puerto Rico esports, Puerto Rico gaming, PREL partners, PREL events",
    ogDescription: "Clear answers about PREL, its content, events, and collaboration opportunities.",
    eyebrow: "Frequently Asked Questions",
    heroTitle: "Clear answers about PREL",
    heroBody:
      "This page answers the most common questions about what Puerto Rico Esports League is, how it operates, what kind of content it produces, and how brands, media, institutions, and audiences can connect with the platform.",
    items: [
      {
        q: "What is Puerto Rico Esports League?",
        a: "Puerto Rico Esports League (PREL) is an esports and entertainment platform in Puerto Rico that produces live events, original content, and television programming around competitive culture.",
      },
      {
        q: "Is PREL just a tournament league?",
        a: "No. While competition is an important part of the project, PREL also works on content, television, live activations, brand collaborations, and community development.",
      },
      {
        q: "Where can I watch PREL content?",
        a: "PREL content lives through its official show, YouTube channel, social platforms, event recaps, and official materials published across its digital channels.",
      },
      {
        q: "Does PREL produce live events in Puerto Rico?",
        a: "Yes. PREL organizes and supports gaming and esports events in Puerto Rico, including tournaments, brand activations, in-person experiences, and audiovisual coverage.",
      },
      {
        q: "Does PREL work with brands, media, and institutions?",
        a: "Yes. PREL collaborates with brands, institutions, media, and strategic partners through sponsorships, branded content, activations, digital visibility, and special projects.",
      },
      {
        q: "Who leads Puerto Rico Esports League?",
        a: "PREL is led by Dwight Santana, the project's president and executive producer, alongside a team focused on events, content, strategic partnerships, and ecosystem growth.",
      },
      {
        q: "How can a brand collaborate with PREL?",
        a: "A brand can collaborate with PREL through sponsorships, content integrations, event presence, in-person activations, or strategic conversations tailored to its goals.",
      },
      {
        q: "Does PREL have reach beyond Puerto Rico?",
        a: "Yes. While it was born in Puerto Rico, PREL is building a platform with the potential to connect with the U.S. Hispanic market, Latin America, and international opportunities.",
      },
    ],
  },
};

export default function FaqPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "es";
  const copy = copyByLang[lang];

  useEffect(() => {
    document.title = copy.title;

    const setMeta = (name, content, isProperty = false) => {
      const selector = isProperty ? `meta[property='${name}']` : `meta[name='${name}']`;
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        if (isProperty) tag.setAttribute("property", name);
        else tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    setMeta("description", copy.description);
    setMeta("keywords", copy.keywords);
    setMeta("og:title", copy.title, true);
    setMeta("og:description", copy.ogDescription, true);
    setMeta("og:type", "website", true);
    setMeta("og:url", "https://www.puertoricoesportleague.com/faq", true);
    setMeta("og:image", "https://www.puertoricoesportleague.com/logo.png", true);

    let canonical = document.head.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://www.puertoricoesportleague.com/faq");

    const schemaId = "prel-faq-schema";
    let schemaTag = document.getElementById(schemaId);
    if (!schemaTag) {
      schemaTag = document.createElement("script");
      schemaTag.type = "application/ld+json";
      schemaTag.id = schemaId;
      document.head.appendChild(schemaTag);
    }
    schemaTag.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: copy.items.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.a,
        },
      })),
    });
  }, [copy]);

  return (
    <div className="min-h-screen bg-[#06070A] text-white">
      <SiteHeader current="/faq" />

      <main className="max-w-[1200px] mx-auto px-6 xl:px-8 pt-12 pb-20">
        <p className="text-xs tracking-[0.3em] uppercase text-[#CE1126]">{copy.eyebrow}</p>
        <h1 className="mt-4 text-[clamp(2.5rem,7vw,5rem)] font-black leading-[0.9] uppercase">
          {copy.heroTitle}
        </h1>
        <p className="mt-5 max-w-3xl text-slate-300 text-lg leading-relaxed">
          {copy.heroBody}
        </p>

        <div className="mt-10 grid gap-4">
          {copy.items.map((item) => (
            <article key={item.q} className="border border-white/10 bg-[#111722] p-6 md:p-7">
              <h2 className="text-xl md:text-2xl font-bold">{item.q}</h2>
              <p className="mt-3 text-slate-300 leading-relaxed">{item.a}</p>
            </article>
          ))}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
